/**
 * What: Renders a deterministic simulated-shopper avatar, with optional Polaris framing.
 * Transport & data-flow: Converts a respondent label into an in-memory SVG data URI for results presentation.
 *
 * `size` keeps the sm/md/lg/xl API (unchanged call sites) and maps to `s-avatar`'s own
 * small/base/large/large-200 scale; `pxBySize` is unrelated and only sizes the generated SVG.
 */

import { memo, useMemo } from 'react'
import { generateAvatarDataUri } from '@testfeed/avatars'

interface RespondentAvatarProps {
  respondentLabel: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  framed?: boolean
}

const pxBySize = {
  sm: 24,
  md: 28,
  lg: 32,
  xl: 40,
} as const

const sAvatarSizeBySize = {
  sm: 'small',
  md: 'base',
  lg: 'large',
  xl: 'large-200',
} as const

/**
 * Renders an avatar for a specific respondent.
 * Memoizes the SVG generation to prevent blocking the main thread during typing or fast rendering.
 */
export const RespondentAvatar = memo(({ respondentLabel, size = 'md', framed = false }: RespondentAvatarProps) => {
  const avatarPixelSize = pxBySize[size]
  const source = useMemo(
    () =>
      generateAvatarDataUri(
        { id: respondentLabel },
        { style: 'notionists', backgroundColor: ['f1f2f3'], size: avatarPixelSize },
      ),
    [respondentLabel, avatarPixelSize],
  )

  const avatar = <s-avatar src={source} alt={respondentLabel} size={sAvatarSizeBySize[size]} />

  if (!framed) return avatar

  return (
    <s-box borderColor="base" borderRadius="small" borderWidth="small" padding="small-100">
      {avatar}
    </s-box>
  )
})
